import { useSnapshot } from "valtio";
import { useNavigate } from "react-router-dom";
import { jobTrackerStore, removeJob, isJobTerminal } from "@store/jobTrackerStore";
import ToastContainer from "react-bootstrap/ToastContainer";
import Toast from "react-bootstrap/Toast";
import ProgressBar from "react-bootstrap/ProgressBar";
import { Paths } from "src/paths";

const JOB_TYPE_LABELS: Record<string, string> = {
  PUBLISH_ENGAGEMENT: "Publishing",
  SYNC_ENGAGEMENT: "Syncing",
  NETWORK_VIEW: "Fetching Network View",
  RENDER: "Rendering",
  RENDER_LAYER: "Rendering",
  IMPORT_PRESETS: "Importing",
  IMPORT_PERFORMANCE: "Syncing Performance",
};

function getJobLabel(type: string): string {
  return JOB_TYPE_LABELS[type] ?? type;
}

function getVariant(status: string, pct: number): string {
  if (status === "COMPLETED") return "success";
  if (status === "FAILED") return "danger";
  if (status === "WARNING") return "warning";
  if (pct >= 66) return "primary";
  if (pct >= 33) return "info";
  return "secondary";
}

export default function ActiveJobsToast() {
  const { activeJobs } = useSnapshot(jobTrackerStore);
  const navigate = useNavigate();

  if (activeJobs.length === 0) return null;

  return (
    <ToastContainer position="top-center" className="p-3" style={{ zIndex: 9999 }}>
      {activeJobs.map((job) => {
        const childrenRunning = job.children?.length > 0 && !isJobTerminal(job);
        const pct = childrenRunning
          ? Math.round(job.children.reduce((s, c) => s + (c.percent_complete || 0), 0) / job.children.length)
          : Math.round(job.percent_complete);
        const isDone = isJobTerminal(job) && job.status === "COMPLETED";
        const isFailed = job.status === "FAILED" || (job.children?.some((c) => c.status === "FAILED") ?? false);
        const isWarning = !isFailed && (job.status === "WARNING" || (job.children?.some((c) => c.status === "WARNING") ?? false));

        return (
          <Toast
            key={job.id}
            bg="light"
            style={{ border: "1px solid #dee2e6", cursor: "pointer", minWidth: "250px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
            onClose={() => removeJob(job.id)}
          >
            <Toast.Header closeButton className="align-items-center" style={{ borderBottom: "none", padding: "0.75rem 0.75rem 0" }}>
              <strong className="me-auto" style={{ fontSize: "0.85rem", lineHeight: 1 }}>
                {getJobLabel(job.type)}
              </strong>
              <small className="text-muted ms-2" style={{ fontSize: "0.75rem", lineHeight: 1 }}>
                {isDone ? "Done" : isFailed ? "Failed" : isWarning ? "Warning" : `${pct}%`}
              </small>
            </Toast.Header>
            <Toast.Body
              style={{ padding: "0.6rem 0.75rem 0.5rem" }}
              onClick={() => navigate(`${import.meta.env.BASE_URL}${Paths.JOBS}/${job.id}`)}
            >
              <ProgressBar
                now={isDone || isFailed || isWarning ? 100 : pct}
                variant={getVariant(job.status, pct)}
                style={{ height: "6px", backgroundColor: "#e0e0e0" }}
              />
              {job.children?.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {job.children.map((child) => {
                    const childPct = Math.round(child.percent_complete);
                    const childDone = child.status === "COMPLETED";
                    const childFailed = child.status === "FAILED";
                    const childWarning = child.status === "WARNING";
                    return (
                      <div key={child.id} style={{ marginBottom: "6px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem", marginBottom: "2px", color: childDone ? "#999" : childFailed ? "#dc3545" : childWarning ? "#d97706" : "#333", fontWeight: childDone || childFailed || childWarning ? 400 : 500 }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: "8px" }}>
                            {child.name || child.type}{child.progress_message ? `: ${child.progress_message}` : ""}
                          </span>
                          <span style={{ flexShrink: 0, color: childDone ? "#22c55e" : childFailed ? "#dc3545" : childWarning ? "#d97706" : undefined }}>
                            {childDone ? "Done" : childFailed ? "Failed" : childWarning ? "Warning" : `${childPct}%`}
                          </span>
                        </div>
                        <ProgressBar
                          now={childDone || childFailed || childWarning ? 100 : childPct}
                          variant={getVariant(child.status, childPct)}
                          style={{ height: "4px", backgroundColor: "#e0e0e0" }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </Toast.Body>
          </Toast>
        );
      })}
    </ToastContainer>
  );
}
